import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { PlanSheet, PlanVersion, Project } from '../types'
import { fmtDate, todayISO } from '../lib/format'
import { consumeNavTarget, flashHighlight } from '../lib/nav-target'

type GlobalSummary = {
  project_name_from_plans?: string | null
  architect?: string | null
  total_sqft?: number | null
  story_count?: number | null
  bedroom_count?: number | null
  bathroom_count?: number | null
  garage_bays?: number | null
  basement?: string | null
  global_summary?: string
  finish_schedule_highlights?: string[]
  structural_highlights?: string[]
  mep_highlights?: string[]
}

export default function PlanVersionsTab({ project }: { project: Project }) {
  const [versions, setVersions] = useState<PlanVersion[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<PlanVersion>>({ date: todayISO() })
  const [uploading, setUploading] = useState(false)
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [sheets, setSheets] = useState<PlanSheet[]>([])
  const [selectedVersion, setSelectedVersion] = useState<PlanVersion | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [opMessage, setOpMessage] = useState<string | null>(null)
  const [forceOpenSheetIndex, setForceOpenSheetIndex] = useState(false)

  useEffect(() => {
    refresh()
  }, [project.id])

  async function refresh() {
    const list = await api.planVersions.list(project.id)
    setVersions(list)
    // Apply nav target if one is queued — pick the requested version,
    // otherwise default to the most recent one with a PDF.
    const t = consumeNavTarget('plans')
    if (t?.planVersionId) {
      const requested = list.find((v) => v.id === t.planVersionId)
      if (requested) {
        await pickVersion(requested)
        if (t.planSheetId) setForceOpenSheetIndex(true)
        // Defer scroll/highlight until sheet table renders
        setTimeout(() => {
          if (t.planSheetId) {
            const el = document.getElementById(`sheet-row-${t.planSheetId}`)
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            flashHighlight(el)
          }
        }, 200)
        return
      }
    }
    if (list.length && !selectedVersion) {
      const v = list.find((x) => x.r2_key) ?? list[0]
      pickVersion(v)
    }
  }

  async function pickVersion(v: PlanVersion) {
    setSelectedVersion(v)
    if (v.r2_key) {
      const s = await api.planVersions.listSheets(project.id, v.id)
      setSheets(s)
    } else {
      setSheets([])
    }
  }

  async function add() {
    if (!draft.version_label) return
    const v = await api.planVersions.create(project.id, draft)
    setVersions((p) => [v, ...p])
    setAdding(false)
    setDraft({ date: todayISO() })
  }

  async function upload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const label = prompt('Plan version label?', `v${versions.length + 1}`)
    if (!label) return
    const summary = prompt('What changed in this revision? (optional)') ?? undefined
    setUploading(true)
    setError(null)
    try {
      const v = await api.planVersions.upload(project.id, file, label, summary)
      setVersions((p) => [v, ...p])
      pickVersion(v)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function runAction(
    v: PlanVersion,
    action: 'analyze' | 'generate-scope' | 'check-compliance'
  ) {
    setBusyVersionId(v.id)
    setBusyAction(action)
    setOpMessage(null)
    setError(null)
    try {
      if (action === 'analyze') {
        await api.planVersions.analyze(v.id)
        setOpMessage('Plan analyzed. Sheet index + global summary stored.')
      } else if (action === 'generate-scope') {
        const replace = confirm(
          'Replace the existing Master Scope items? (Cancel = append)'
        )
        const r = await api.planVersions.generateScope(v.id, replace)
        setOpMessage(
          `Generated: ${r.scope_items_inserted} scope items, ${r.scope_gaps_inserted} gaps, ${r.selections_inserted} selections.`
        )
      } else if (action === 'check-compliance') {
        const r = await api.planVersions.checkCompliance(v.id)
        setOpMessage(`Compliance: ${r.flags_inserted} flags raised, ${r.passed_checks.length} passed.`)
      }
      await refresh()
      const fresh = (await api.planVersions.list(project.id)).find((x) => x.id === v.id)
      if (fresh) pickVersion(fresh)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusyVersionId(null)
      setBusyAction(null)
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this plan version (and any uploaded PDF)?')) return
    await api.planVersions.delete(id)
    setVersions((p) => p.filter((x) => x.id !== id))
    if (selectedVersion?.id === id) {
      setSelectedVersion(null)
      setSheets([])
    }
  }

  const summary: GlobalSummary | null = (() => {
    if (!selectedVersion?.ai_global_summary) return null
    try {
      return JSON.parse(selectedVersion.ai_global_summary) as GlobalSummary
    } catch {
      return null
    }
  })()

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Plans</h2>
          <p className="muted small">
            {versions.length} versions · upload a PDF to run AI analysis, generate Master Scope,
            and check Spec Book compliance.
          </p>
        </div>
        <div className="row gap-2">
          <label className="btn-primary" style={{ cursor: 'pointer' }}>
            {uploading ? 'Uploading…' : '↑ Upload PDF'}
            <input
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={upload}
              disabled={uploading}
            />
          </label>
          <button className="btn-ghost" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Drive link'}
          </button>
        </div>
      </div>

      {error && <div className="card alert mb-2">{error}</div>}
      {opMessage && <div className="card mb-2"><strong>OK.</strong> {opMessage}</div>}

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Version label</span>
            <input
              value={draft.version_label ?? ''}
              onChange={(e) => setDraft({ ...draft, version_label: e.target.value })}
              placeholder="v3"
            />
          </label>
          <label className="form-row">
            <span>Date</span>
            <input
              type="date"
              value={draft.date?.slice(0, 10) ?? todayISO()}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Change summary</span>
            <textarea
              rows={3}
              value={draft.change_summary ?? ''}
              onChange={(e) => setDraft({ ...draft, change_summary: e.target.value })}
              placeholder="Moved master closet door. Added pantry. Resized kitchen island."
            />
          </label>
          <label className="form-row">
            <span>Google Drive URL</span>
            <input
              type="url"
              value={draft.file_url ?? ''}
              onChange={(e) => setDraft({ ...draft, file_url: e.target.value })}
              placeholder="https://drive.google.com/file/d/..."
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add version
          </button>
        </div>
      )}

      {versions.length === 0 && !adding && (
        <p className="muted">
          No plan versions yet. Upload a PDF or paste a Drive link to start.
        </p>
      )}

      <div className="row" style={{ alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: '1 1 320px', maxWidth: 380 }}>
          {versions.map((v) => (
            <div
              key={v.id}
              className="card mb-2"
              onClick={() => pickVersion(v)}
              style={{
                cursor: 'pointer',
                borderLeft:
                  selectedVersion?.id === v.id
                    ? '3px solid var(--gold, #d4af37)'
                    : '3px solid transparent',
              }}
            >
              <div className="row-between">
                <div>
                  <div className="row-title">
                    {v.version_label} · {fmtDate(v.date)}
                  </div>
                  {v.change_summary && <div className="mt-1 small">{v.change_summary}</div>}
                  <div className="mt-1 muted small">
                    {v.r2_key ? `📄 PDF (${v.pdf_size ? Math.round(v.pdf_size / 1024) + 'KB' : '?'})` : ''}
                    {v.r2_key && v.sheet_count != null ? ` · ${v.sheet_count} sheets` : ''}
                    {v.analyzed_at ? ` · analyzed ${fmtDate(v.analyzed_at)}` : ''}
                    {v.ai_error ? ` · ⚠ ${v.ai_error.slice(0, 80)}` : ''}
                  </div>
                  {v.file_url && (
                    <div className="mt-1">
                      <a href={v.file_url} target="_blank" rel="noopener noreferrer">
                        Drive link →
                      </a>
                    </div>
                  )}
                  {v.r2_key && (
                    <div className="mt-1">
                      <a href={api.fileUrl(v.r2_key)} target="_blank" rel="noopener noreferrer">
                        Open uploaded PDF →
                      </a>
                    </div>
                  )}
                </div>
                <button
                  className="btn-ghost small"
                  onClick={(e) => {
                    e.stopPropagation()
                    remove(v.id)
                  }}
                >
                  ✕
                </button>
              </div>
              {v.r2_key && (
                <div className="row mt-2" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn-ghost small"
                    onClick={(e) => {
                      e.stopPropagation()
                      runAction(v, 'analyze')
                    }}
                    disabled={busyVersionId === v.id}
                  >
                    {busyVersionId === v.id && busyAction === 'analyze'
                      ? 'Analyzing…'
                      : v.analyzed_at
                      ? '↻ Re-analyze'
                      : 'Analyze with AI'}
                  </button>
                  {v.analyzed_at && (
                    <>
                      <button
                        className="btn-ghost small"
                        onClick={(e) => {
                          e.stopPropagation()
                          runAction(v, 'generate-scope')
                        }}
                        disabled={busyVersionId === v.id}
                      >
                        {busyVersionId === v.id && busyAction === 'generate-scope'
                          ? 'Generating…'
                          : 'Generate Master Scope'}
                      </button>
                      <button
                        className="btn-ghost small"
                        onClick={(e) => {
                          e.stopPropagation()
                          runAction(v, 'check-compliance')
                        }}
                        disabled={busyVersionId === v.id}
                      >
                        {busyVersionId === v.id && busyAction === 'check-compliance'
                          ? 'Checking…'
                          : 'Check Spec compliance'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ flex: '2 1 480px' }}>
          {selectedVersion?.analyzed_at && summary ? (
            <div className="card">
              <h3 style={{ margin: 0 }}>{selectedVersion.version_label} — AI Summary</h3>
              <p className="muted small">
                {fmtDate(selectedVersion.analyzed_at!)} · {sheets.length} sheets
              </p>
              <div className="row mt-2" style={{ flexWrap: 'wrap', gap: '0.6rem' }}>
                <Stat label="Architect" value={summary.architect} />
                <Stat label="Sqft" value={summary.total_sqft} />
                <Stat label="Stories" value={summary.story_count} />
                <Stat label="Beds" value={summary.bedroom_count} />
                <Stat label="Baths" value={summary.bathroom_count} />
                <Stat label="Garage" value={summary.garage_bays} />
                <Stat label="Basement" value={summary.basement} />
              </div>
              {summary.global_summary && <p className="mt-2">{summary.global_summary}</p>}

              <details className="mt-2" open={forceOpenSheetIndex}>
                <summary className="muted small" style={{ cursor: 'pointer' }}>
                  Sheet index ({sheets.length})
                </summary>
                <table className="mt-1" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Page</th>
                      <th style={{ textAlign: 'left' }}>Sheet</th>
                      <th style={{ textAlign: 'left' }}>Title</th>
                      <th style={{ textAlign: 'left' }}>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheets.map((s) => (
                      <tr key={s.id} id={`sheet-row-${s.id}`}>
                        <td>
                          {selectedVersion?.r2_key ? (
                            <a
                              href={`${api.fileUrl(selectedVersion.r2_key)}#page=${s.page_in_pdf}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              p{s.page_in_pdf}
                            </a>
                          ) : (
                            s.page_in_pdf
                          )}
                        </td>
                        <td>{s.sheet_number ?? '—'}</td>
                        <td>{s.sheet_title ?? '—'}</td>
                        <td>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              padding: '1px 6px',
                              background: 'rgba(212,175,55,0.2)',
                              borderRadius: 3,
                            }}
                          >
                            {s.sheet_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>

              {summary.finish_schedule_highlights && summary.finish_schedule_highlights.length > 0 && (
                <details className="mt-2">
                  <summary className="muted small" style={{ cursor: 'pointer' }}>
                    Finish schedule highlights ({summary.finish_schedule_highlights.length})
                  </summary>
                  <ul>
                    {summary.finish_schedule_highlights.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </details>
              )}
              {summary.structural_highlights && summary.structural_highlights.length > 0 && (
                <details className="mt-2">
                  <summary className="muted small" style={{ cursor: 'pointer' }}>
                    Structural highlights ({summary.structural_highlights.length})
                  </summary>
                  <ul>
                    {summary.structural_highlights.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </details>
              )}
              {summary.mep_highlights && summary.mep_highlights.length > 0 && (
                <details className="mt-2">
                  <summary className="muted small" style={{ cursor: 'pointer' }}>
                    MEP highlights ({summary.mep_highlights.length})
                  </summary>
                  <ul>
                    {summary.mep_highlights.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ) : selectedVersion?.r2_key ? (
            <div className="card">
              <p className="muted">
                PDF uploaded but not yet analyzed. Click <strong>Analyze with AI</strong> on the
                version card to extract the sheet index, global summary, and finish schedule.
              </p>
            </div>
          ) : selectedVersion ? (
            <div className="card">
              <p className="muted">
                Drive-link version. To run AI analysis, upload a PDF version using the Upload PDF
                button above.
              </p>
            </div>
          ) : (
            <p className="muted">Select a plan version to see details.</p>
          )}
        </div>
      </div>
    </section>
  )
}

function Stat({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="muted small" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: '0.95rem' }}>{value == null || value === '' ? '—' : value}</div>
    </div>
  )
}
